<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import VideoElement from './VideoElement.vue';
import VideoOverlay from './VideoOverlay.vue';
import VideoControls from './VideoControls.vue';
import Hls from 'hls.js';
import HLSManager from '@/Services/VideoPlayer/HLSManager';
import VASTManager from '@/Services/VideoPlayer/VASTManager';
import SourceManager from '@/Services/VideoPlayer/SourceManager';
import PlaybackManager from '@/Services/VideoPlayer/PlaybackManager';
import PreloadManager from '@/Services/VideoPlayer/PreloadManager';
import { useVideoPlayerStore } from '@/Stores/VideoPlayerStore';
import { useAdDisplayStore } from '@/Stores/AdDisplayStore';

const props = defineProps({
    source: { type: Object, required: true }, // Single source: {url, type, mimetype}
    poster: { type: String },
    autoplay: { type: Boolean, default: false },
    loop: { type: Boolean, default: false },
    muted: { type: Boolean, default: true },
    playsinline: { type: Boolean, default: true },
    preload: { type: String, default: 'auto' },
    isActive: { type: Boolean, default: false },
    initialTime: { type: Number, default: 0.1 },
    vastUrl: { type: String, default: null },
    adFrequency: { type: Number, default: -1 },
    orientation: { type: String, default: 'vertical' }, // vertical, horizontal, square
    showProgress: { type: Boolean, default: true },
});

const emit = defineEmits(['mounted', 'ended', 'timeupdate', 'pause', 'play', 'ad-started', 'ad-ended']);

const playerStore = useVideoPlayerStore();
const adDisplayStore = useAdDisplayStore();

const mainVideo = ref(null);
const adVideo = ref(null);

const hlsManager = new HLSManager();
const vastManager = new VASTManager();
const sourceManager = new SourceManager();
const playbackManager = new PlaybackManager();

const showPlayButton = ref(true);
const isLoading = ref(false);
const adPlayedOrAttempted = ref(false);
const pauseTimeoutId = ref(null);
const hasStartedPlaying = ref(false); // Track if video has ever started playing
const hasUserGesture = ref(false); // Track if we have a user gesture

const currentSource = computed(() => sourceManager.getCurrentSource());
const videoSrc = computed(() => {
    if (!currentSource.value) return '';
    const url = sourceManager.getSourceUrl(props.initialTime);

    return url;
});

const initializePlayer = async () => {
    if (!mainVideo.value?.videoEl) return;

    const video = mainVideo.value.videoEl;


    sourceManager.setSource(props.source);
    const source = sourceManager.getCurrentSource();

    if (!source) {
        console.error('[VideoPlayer] No source available');
        return;
    }

    playbackManager.setVideo(video);
    playbackManager.on('onStateChange', handleStateChange);
    playbackManager.on('onTimeUpdate', handleTimeUpdate);
    playbackManager.on('onEnded', handleEnded);
    playbackManager.on('onError', handleError);

    video.muted = playerStore.getInitialMuteState();

    if (source.isHLS) {
        // Check for native HLS support (iOS/Safari)
        const hasNativeHLS = HLSManager.hasNativeSupport(video);

        if (hasNativeHLS) {
            // iOS/Safari: Native HLS - just like MP4
            console.log('[VideoPlayer] Using native HLS support (iOS/Safari)');
            video.src = source.url;
            video.load();
        } else {
            // Desktop: HLS.js - simple initialization
            console.log('[VideoPlayer] Using HLS.js');
            const hlsInitialized = hlsManager.init(video, source.url);

            if (!hlsInitialized) {
                console.error('[VideoPlayer] HLS not supported');
            }
        }
    } else {
        // MP4: Simple and works perfectly
        const url = sourceManager.getSourceUrl(props.initialTime);
        video.src = url;
        video.load();
    }

    if (props.vastUrl && adVideo.value?.videoEl) {
        vastManager.setAdVideo(adVideo.value.videoEl);
        vastManager.on('onAdStart', handleAdStart);
        vastManager.on('onAdEnd', handleAdEnd);
        vastManager.on('onAdError', handleAdError);
        vastManager.on('onAdClick', handleAdClick);
    }

    if (source.isHLS && props.initialTime > 0) {
        video.addEventListener('loadedmetadata', () => {
            video.currentTime = props.initialTime;
        }, { once: true });
    }

    emit('mounted', {
        player: video,
        state: playbackManager.getState()
    });

    // Always attempt to play if active (iOS requires programmatic play, not autoplay attribute)
    if (props.isActive) {
        await nextTick();
        await play();
    }
};

const play = async () => {
    if (!mainVideo.value?.videoEl || !playbackManager.video) {
        return false;
    }

    // Handle VAST ads (KEEP THIS)
    if (props.vastUrl && !adPlayedOrAttempted.value) {
        const shouldShowAd = adDisplayStore.shouldShowVastAd(props.adFrequency);

        if (shouldShowAd) {
            adPlayedOrAttempted.value = true;
            isLoading.value = true;

            try {
                await vastManager.loadAd();
                await vastManager.playAd();
                return true;
            } catch (error) {
                isLoading.value = false;
            }
        }
    }

    adPlayedOrAttempted.value = true;
    clearPauseTimeout();
    showPlayButton.value = false;

    const success = await playbackManager.play();
    if (!success) {
        showPlayButton.value = true;
    }

    return success;
};

const pause = () => {
    playbackManager.pause();
};

const togglePlay = () => {

    hasUserGesture.value = true;

    if (playbackManager.getState().paused) {
        play();
    } else {
        pause();
    }
};

const toggleMute = () => {
    playbackManager.toggleMute();
    playerStore.setGlobalMuteState(!playbackManager.getState().muted);
};

const reset = () => {
    vastManager.cleanup();
    playbackManager.reset(props.initialTime);
    adPlayedOrAttempted.value = false;
    showPlayButton.value = true;
    hasStartedPlaying.value = false; // Reset this flag on video reset
    isLoading.value = false; // Ensure loading is cleared
    clearPauseTimeout();
};

const resetAdState = () => {
    vastManager.cleanup();
    adPlayedOrAttempted.value = false;
    playerStore.resetAdState();
};

const seek = (time) => {
    playbackManager.seek(time);
};

const handleStateChange = (state) => {
    playerStore.updatePlayerState(state);

    if (state.playing) {
        hasStartedPlaying.value = true;
    }

    if (state.paused) {
        clearPauseTimeout();
        pauseTimeoutId.value = setTimeout(() => {
            if (playbackManager.getState().paused) {
                showPlayButton.value = true;
            }
        }, 200);
    } else {
        clearPauseTimeout();
        showPlayButton.value = false;
    }

    if (hasStartedPlaying.value) {
        isLoading.value = false; // Never show loading after video has started
    } else {
        isLoading.value = state.loading;
    }
};

const handleTimeUpdate = (data) => {
    emit('timeupdate', { 
        player: mainVideo.value?.videoEl, 
        state: playbackManager.getState() 
    });
};

const handleEnded = () => {
    showPlayButton.value = true;
    emit('ended');
};

const handleError = (error) => {
    isLoading.value = false;

    console.error('[VideoPlayer] Video error occurred:', {
        isHLS: sourceManager.isCurrentSourceHLS(),
        currentSource: sourceManager.getCurrentSource(),
        error: error,
        videoError: mainVideo.value?.videoEl?.error,
    });

    // Trust HLS.js to handle all errors - no manual intervention
};

const handleAdStart = () => {
    playerStore.updateAdState({ playing: true });
    emit('ad-started');
};

const handleAdEnd = () => {
    playerStore.resetAdState();
    emit('ad-ended');
    play(); // Resume main video
};

const handleAdError = () => {
    isLoading.value = false;
    playerStore.resetAdState();
    emit('ad-ended');
    play(); // Continue with main video
};

const handleAdClick = () => {
    vastManager.handleAdClick();
};

const handleSkipAd = () => {
    vastManager.skipAd();
};

const handleAdTimeUpdate = () => {
    if (!adVideo.value?.videoEl) return;
    const video = adVideo.value.videoEl;
    
    playerStore.updateAdState({
        currentTime: video.currentTime,
        duration: video.duration || 0,
        canSkip: video.currentTime > 5,
    });
};

const clearPauseTimeout = () => {
    if (pauseTimeoutId.value) {
        clearTimeout(pauseTimeoutId.value);
        pauseTimeoutId.value = null;
    }
};

watch(() => props.isActive, async (active) => {
    // Always attempt to play when active (iOS requires programmatic play)
    if (active) {
        await nextTick();
        play();
    } else if (!active) {
        pause();

    }
});

watch(() => props.source, (newSource, oldSource) => {

    
    if (!newSource) return;
    
    sourceManager.setSource(newSource);
    sourceManager.reset();
    
    if (mainVideo.value?.videoEl) {
        const source = sourceManager.getCurrentSource();
        if (source) {
            if (source.isHLS) {
                hlsManager.destroy();
                hlsManager.init(mainVideo.value.videoEl, source.url);
            } else {
                hlsManager.destroy();
                mainVideo.value.videoEl.src = sourceManager.getSourceUrl(props.initialTime);
                mainVideo.value.videoEl.load();
            }
        }
    }
}, { deep: true });

defineExpose({
    play,
    pause,
    reset,
    resetAdState,
    seek,
    toggleMute,
    player: computed(() => mainVideo.value?.videoEl),
    state: computed(() => playbackManager.getState()),
});

const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const isAndroidChrome = /Android.*Chrome/i.test(navigator.userAgent);

const handleVisibilityChange = () => {
    if (document.hidden) {
        if (playbackManager.getState().playing) {
            pause();
        }
    } else {

    }
};

const handleNetworkChange = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {

        if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
            if (hlsManager.hls && hlsManager.hls.levels.length > 0) {
                hlsManager.hls.currentLevel = 0; // Force lowest quality
            }
        }
    }
};

const handleMemoryPressure = () => {
    if ('memory' in performance && performance.memory.usedJSHeapSize > performance.memory.jsHeapSizeLimit * 0.8) {
        console.warn('[VideoPlayer] Memory pressure detected, clearing buffers');

        if (hlsManager.hls) {
            hlsManager.hls.trigger(Hls.Events.BUFFER_FLUSHING, {
                startOffset: 0,
                endOffset: Number.POSITIVE_INFINITY,
            });
        }

        PreloadManager.clearAll();
    }
};

// Removed IntersectionObserver and touch handling - unnecessary complexity

onMounted(async () => {
    await nextTick();
    await initializePlayer();
    playerStore.setActivePlayer({ play, pause, reset, toggleMute });

    if (isMobile) {
        document.addEventListener('visibilitychange', handleVisibilityChange);

        if ('connection' in navigator) {
            navigator.connection.addEventListener('change', handleNetworkChange);
        }

        setInterval(handleMemoryPressure, 30000); // Every 30 seconds
    }
});

onBeforeUnmount(() => {
    clearPauseTimeout();
    hlsManager.destroy();
    vastManager.cleanup();
    playbackManager.destroy();
    playerStore.setActivePlayer(null);

    if (isMobile) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);

        if ('connection' in navigator) {
            navigator.connection.removeEventListener('change', handleNetworkChange);
        }
    }
});
</script>

<template>
    <div class="relative w-full h-full bg-black overflow-hidden">
        <!-- Main Video -->
        <div class="absolute inset-0 select-none">
            <VideoElement
                v-show="!playerStore.adState.playing"
                ref="mainVideo"
                :src="currentSource && !currentSource.isHLS ? videoSrc : ''"
                :poster="poster"
                :loop="loop"
                :muted="playerStore.playerState.muted"
                :playsinline="playsinline"
                :preload="preload"
                :orientation="orientation"
            />
        </div>

        <!-- Ad Video -->
        <div class="absolute inset-0 select-none">
            <VideoElement
                v-show="playerStore.adState.playing"
                ref="adVideo"
                :muted="true"
                :playsinline="true"
                :is-ad="true"
                @timeupdate="handleAdTimeUpdate"
            />
        </div>

        <!-- Overlay (play button, loading, ad UI) -->
        <VideoOverlay
            :show-play-button="showPlayButton"
            :is-loading="isLoading"
            :is-ad-playing="playerStore.adState.playing"
            :ad-duration="playerStore.adState.duration"
            :ad-current-time="playerStore.adState.currentTime"
            :can-skip-ad="playerStore.adState.canSkip"
            class="absolute inset-0 pointer-events-auto"
            @play="play"
            @toggle-play="togglePlay"
            @skip-ad="handleSkipAd"
            @ad-click="handleAdClick"
        />

        <!-- Controls (only progress bar, no audio control) -->
        <VideoControls
            v-if="!playerStore.adState.playing"
            :is-muted="playerStore.playerState.muted"
            :current-time="playerStore.playerState.currentTime"
            :duration="playerStore.playerState.duration"
            :show-mute-button="false"
            :show-progress="showProgress"
            class="absolute bottom-0 inset-x-0"
            @toggle-mute="toggleMute"
        />
    </div>
</template>